/**
 * Pestaña "Estudios Contables" del Panel de Plataforma (platform-admin).
 *
 * Lista los estudios con su conteo de cartera/equipo, permite crear uno nuevo
 * (asignando su primer administrador) y abrir el detalle para gestionar empresas,
 * equipo y solicitudes. Es la UI que reemplaza el bootstrap por shell (#105).
 */
import { useMemo, useState } from 'react'
import { Plus, Search, Loader2, Briefcase, Inbox } from 'lucide-react'
import { Button } from '@/components/Button'
import { ToastContainer, useToast } from '@/components/Toast'
import { usePlatformFirms, usePlatformRequests } from '@/modules/firm/hooks/useFirmAdmin'
import { CreateFirmModal } from './CreateFirmModal'
import { FirmAdminDetailModal } from './FirmAdminDetailModal'
import { FirmStatusBadge } from './firmAdminShared'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/ds/DataTable'

export function FirmsAdminTab() {
  const { data: firms, isLoading } = usePlatformFirms()
  const { data: pendingRequests } = usePlatformRequests('pending')
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = firms ?? []
    if (!q) return list
    return list.filter((f) => f.name.toLowerCase().includes(q) || (f.tax_id ?? '').toLowerCase().includes(q))
  }, [firms, search])

  const pendingCount = pendingRequests?.length ?? 0

  return (
    <div>
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar estudio por nombre o RUT"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar estudio por nombre o RUT…"
            className="w-full pl-8 pr-4 py-2 rounded-lg border border-divider-strong text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
            <Inbox size={13} /> {pendingCount} solicitud(es) pendiente(s)
          </span>
        )}
        <Button variant="info" onClick={() => setCreateOpen(true)} leftIcon={<Plus size={15} />} className="whitespace-nowrap">
          Crear estudio
        </Button>
      </div>

      <DataTable
        rows={filtered}
        rowKey={f => f.id}
        loading={isLoading}
        loadingNode={
          <div className="text-center py-12 text-content-subtle">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" /> Cargando estudios…
          </div>
        }
        minWidth={760}
        caption="Estudios contables de la plataforma"
        empty={
          <EmptyState
            icon={Briefcase}
            title={search ? 'Sin resultados.' : 'No hay estudios contables creados aún.'}
            size="md"
          />
        }
        columns={[
          {
            key: 'name', header: 'Estudio', sortable: true,
            render: f => <p className="font-medium text-content">{f.name}</p>,
          },
          {
            key: 'tax_id', header: 'RUT', sortable: true,
            render: f => <span className="text-xs text-content-muted">{f.tax_id || '—'}</span>,
          },
          {
            key: 'clients_count', header: 'Empresas', numeric: true, sortable: true,
            sortValue: f => f.clients_count ?? 0,
            render: f => <span className="text-xs text-content-muted">{f.clients_count ?? 0}</span>,
          },
          {
            key: 'staff_count', header: 'Equipo', numeric: true, sortable: true,
            sortValue: f => f.staff_count ?? 0,
            render: f => <span className="text-xs text-content-muted">{f.staff_count ?? 0}</span>,
          },
          {
            key: 'status', header: 'Estado', sortable: true, width: 120,
            render: f => <FirmStatusBadge status={f.status} />,
          },
          {
            key: 'gestionar', header: <span className="sr-only">Gestionar</span>, width: 110,
            render: f => (
              <div className="text-right">
                <button
                  onClick={() => setDetailId(f.id)}
                  className="px-2.5 py-1 text-xs text-primary hover:bg-primary-50 rounded-lg transition-colors"
                >
                  Gestionar
                </button>
              </div>
            ),
          },
        ]}
      />
      {!isLoading && filtered.length > 0 && (
        <p className="mt-3 text-xs text-content-subtle">{filtered.length} estudio(s)</p>
      )}

      <CreateFirmModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(firm) => setDetailId(firm.id)}
        toast={toast}
      />
      {detailId && (
        <FirmAdminDetailModal firmId={detailId} onClose={() => setDetailId(null)} toast={toast} />
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

export default FirmsAdminTab
